/**
 * Comercial → Leads → Perfil do lead — página cheia (v2).
 *
 * Espelha ClienteView.jsx (mesmo layout: coluna de identidade/métricas à
 * esquerda + abas com histórico/atividades/negócios/arquivos/atendimentos à
 * direita) — reusa clienteView.css (prefixo `cv-`) e as peças de panelKit.jsx,
 * sem duplicar CSS. Substitui o antigo drawer lateral (LeadPanel) — a
 * navegação agora é uma rota própria (`/comercial/leads/:id`), igual à de
 * Clientes.
 *
 * Um Lead é o mesmo `contact` do Cliente, só que ainda sem negócio ganho —
 * reusa os mesmos endpoints (getCrmContact/…Deals/…Activities/…History/
 * updateCrmContact/deleteCrmContact). Não tem aba "Projetos" (módulo de
 * entrega é exclusivo de quem já é cliente).
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import AdOriginCard from '../../components/AdOriginCard.jsx'
import LeadCustomFields from '../../components/LeadCustomFields.jsx'
import LeadCustomFieldsManager from '../../components/LeadCustomFieldsManager.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  getCrmContact,
  getLeadCustomFields,
  getCrmMembers,
  getCrmContactDeals,
  getCrmContactActivities,
  createCrmContactActivity,
  updateActivity,
  getCrmContactHistory,
  updateCrmContact,
  deleteCrmContact,
  deleteDeal,
  getPipelines,
  getStages,
  fmtBRL,
} from '../../db/api.js'
import {
  STATUS_COLORS, HIST_ICON, HIST_COLOR, fmtDate, fmtDateTime,
  DealsTab, FilesTab, ActivitiesTab, ServiceTab, ConfirmDialog,
} from './panelKit.jsx'
import AgendaEventModal from '../agenda/AgendaEventModal.jsx'
import ClienteFormPanel from './ClienteFormPanel.jsx'
import DealPanel from './DealPanel.jsx'
import DealModal from './DealModal.jsx'
import './clienteView.css'

const TABS = [
  { id: 'historico', label: 'Histórico' },
  { id: 'atividades', label: 'Atividades' },
  { id: 'negocios', label: 'Negócios' },
  { id: 'arquivos', label: 'Arquivos' },
  { id: 'atendimentos', label: 'Atendimentos' },
]

const FILTROS = [
  { id: 'todos', label: 'Todos' },
  { id: 'notas', label: 'Notas' },
  { id: 'atividades', label: 'Atividades' },
  { id: 'alteracoes', label: 'Alterações' },
]

const STATUS_LABELS = {
  prospect: 'Prospect', lead: 'Lead', negociacao: 'Negociação',
  cliente: 'Cliente', recorrente: 'Recorrente', churned: 'Encerrado', arquivado: 'Arquivado',
}

const iniciais = (nome) => String(nome || '?').trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?'

const AVATAR_CORES = ['#101828', '#2563eb', '#7c5cf0', '#12a06a']
function corAvatar(id) {
  const s = String(id || '')
  let h = 0
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AVATAR_CORES[h % AVATAR_CORES.length]
}

function origemIcon(origem) {
  const o = (origem || '').toLowerCase()
  if (o.includes('whats')) return 'whats'
  if (o.includes('indic')) return 'star'
  if (o.includes('site')) return 'link'
  if (o.includes('anúncio') || o.includes('anuncio') || o.includes('ads')) return 'ads'
  if (o.includes('evento')) return 'calendar'
  if (o.includes('parceria')) return 'users'
  return 'target'
}

const isNota = (item) => item.tipo === 'atividade' && (item.descricao || '').startsWith('mensagem')

function classifica(item) {
  if (isNota(item)) return 'notas'
  if (item.tipo === 'atividade') return 'atividades'
  if (item.tipo === 'negocio') return 'alteracoes'
  return 'sistema'
}

function iconFor(item) {
  if (isNota(item)) return { icon: 'page', color: '#f0a125' }
  return { icon: HIST_ICON[item.tipo] || HIST_ICON.default, color: HIST_COLOR[item.tipo] || HIST_COLOR.default }
}

function cicloDias(deals) {
  const datas = deals
    .filter((d) => d.status === 'ganho')
    .map((d) => new Date(d.data_fechamento || d.updated_at))
    .filter((d) => !isNaN(d))
    .sort((a, b) => a - b)
  if (datas.length < 2) return null
  let total = 0
  for (let i = 1; i < datas.length; i += 1) total += datas[i] - datas[i - 1]
  return Math.round(total / (datas.length - 1) / 86400000)
}

// ── Aba Histórico: composer de nota + feed unificado (mesmo componente de
// ClienteView.jsx, duplicado aqui pois é local ao módulo — evita remontar o
// pai e perder o texto da nota em digitação a cada re-render).
function HistoricoTab({ contactId, user, history, tabsLoading, activityIdByKey, onReload, onError }) {
  const [noteText, setNoteText] = useState('')
  const [saving, setSaving] = useState(false)
  const [filtro, setFiltro] = useState('todos')
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  const salvarNota = async () => {
    if (!noteText.trim() || saving) return
    setSaving(true)
    try {
      await createCrmContactActivity(contactId, {
        titulo: noteText.trim(), tipo: 'mensagem', prioridade: 'baixa', status: 'feito',
        responsavel_id: user?.id || null,
      })
      setNoteText('')
      await onReload()
    } catch (e) { onError(e.message) } finally { setSaving(false) }
  }

  const startEdit = (item) => {
    const key = `${item.titulo}__${new Date(item.occurred_at).getTime()}`
    const activityId = activityIdByKey.get(key)
    if (!activityId) return
    setEditingId(activityId)
    setEditText(item.titulo)
  }

  const saveEdit = async () => {
    if (!editText.trim() || savingEdit) return
    setSavingEdit(true)
    try {
      await updateActivity(editingId, { titulo: editText.trim() })
      setEditingId(null)
      await onReload()
    } catch (e) { onError(e.message) } finally { setSavingEdit(false) }
  }

  const visiveis = history.filter((h) => filtro === 'todos' || classifica(h) === filtro)

  return (
    <div>
      <div className="cv-note-box">
        <div className="cv-note-head">
          <span className="cv-note-title">Adicionar nota</span>
          <button className="cv-note-pin" title="Fixar (em breve)" disabled><Icon n="star" s={14} /></button>
        </div>
        <textarea
          className="cv-note-textarea"
          placeholder="Escreva uma nota sobre este lead…"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
        />
        <div className="cv-note-foot">
          <div className="cv-note-tools">
            <button className="cv-note-tool" disabled title="Anexo (em breve)"><Icon n="clip" s={15} /></button>
            <button className="cv-note-tool" disabled title="Emoji (em breve)"><Icon n="smile" s={15} /></button>
          </div>
          <div className="cv-note-actions">
            <button className="cv-btn cv-btn-ghost" onClick={() => setNoteText('')} disabled={saving || !noteText}>Cancelar</button>
            <button className="cv-btn cv-btn-primary" onClick={salvarNota} disabled={saving || !noteText.trim()}>
              {saving ? 'Salvando…' : 'Salvar nota'}
            </button>
          </div>
        </div>
      </div>

      <div className="cv-filters">
        {FILTROS.map((f) => (
          <button key={f.id} type="button" className={`cv-filter${filtro === f.id ? ' active' : ''}`} onClick={() => setFiltro(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {tabsLoading ? (
        <div className="cv-empty">Carregando…</div>
      ) : !visiveis.length ? (
        <div className="cv-empty">Nenhum evento por aqui ainda.</div>
      ) : (
        <div className="cv-timeline">
          {visiveis.map((item, i) => {
            const { icon, color } = iconFor(item)
            const nota = isNota(item)
            const last = i === visiveis.length - 1
            const key = `${item.titulo}__${new Date(item.occurred_at).getTime()}`
            const canEdit = nota && activityIdByKey.has(key)
            const isEditing = canEdit && editingId === activityIdByKey.get(key)
            return (
              <div key={i} className="cv-tl-item">
                <div className="cv-tl-rail">
                  <div className="cv-tl-icon" style={{ background: `${color}1a`, borderColor: `${color}55`, color }}>
                    <Icon n={icon} s={17} c={color} />
                  </div>
                  {!last && <div className="cv-tl-line" />}
                </div>
                <div className="cv-tl-body">
                  <div className={`cv-tl-card${item.ator ? '' : ' plain'}`}>
                    <div className="cv-tl-head">
                      {item.ator ? (
                        <div className="cv-tl-who">
                          <span className="cv-tl-avatar" style={{ background: corAvatar(item.ator) }}>{iniciais(item.ator)}</span>
                          <span className="cv-tl-name">{item.ator}</span>
                        </div>
                      ) : (
                        <div className="cv-tl-who">
                          <span className="cv-tl-name">{item.titulo}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span className="cv-tl-time">{fmtDateTime(item.occurred_at)}</span>
                        {canEdit && !isEditing && (
                          <span className="cv-tl-hover">
                            <button className="cv-tl-iconbtn" title="Editar nota" onClick={() => startEdit(item)}>
                              <Icon n="edit" s={13} />
                            </button>
                          </span>
                        )}
                      </div>
                    </div>

                    {!item.ator && !nota && item.descricao && <div className="cv-tl-sub">{item.descricao}</div>}

                    {isEditing ? (
                      <div className="cv-tl-edit-box">
                        <textarea
                          className="cv-note-textarea"
                          style={{ minHeight: 70 }}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                        />
                        <div className="cv-tl-edit-actions">
                          <button className="cv-btn cv-btn-ghost" onClick={() => setEditingId(null)} disabled={savingEdit}>Cancelar</button>
                          <button className="cv-btn cv-btn-primary" onClick={saveEdit} disabled={savingEdit || !editText.trim()}>
                            {savingEdit ? 'Salvando…' : 'Salvar'}
                          </button>
                        </div>
                      </div>
                    ) : nota ? (
                      <div className="cv-tl-text">{item.titulo}</div>
                    ) : item.ator ? (
                      <div className="cv-tl-text">{item.titulo}{item.descricao ? ` — ${item.descricao}` : ''}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function LeadView() {
  const { id: contactId } = useParams()
  const navigate = useNavigate()
  const { selectedClient, user } = useApp()

  const [contact, setContact] = useState(null)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [tab, setTab] = useState('historico')

  const [history, setHistory] = useState([])
  const [activities, setActivities] = useState([])
  const [deals, setDeals] = useState([])
  const [customDefs, setCustomDefs] = useState([])
  const [camposOpen, setCamposOpen] = useState(false)
  const [tabsLoading, setTabsLoading] = useState(true)
  const [infoDealId, setInfoDealId] = useState(null)

  const [editOpen, setEditOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [dealModal, setDealModal] = useState(null)
  const [agendaOpen, setAgendaOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const load = () => {
    if (!contactId) return
    setLoading(true); setTabsLoading(true); setErr(null)
    getCrmContact(contactId)
      .then(setContact)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false))

    Promise.allSettled([
      getCrmContactHistory(contactId),
      getCrmContactActivities(contactId),
      getCrmContactDeals(contactId),
    ]).then(([h, a, d]) => {
      setHistory(h.status === 'fulfilled' ? h.value : [])
      setActivities(a.status === 'fulfilled' ? a.value : [])
      setDeals(d.status === 'fulfilled' ? d.value : [])
      setTabsLoading(false)
    })
  }

  useEffect(() => { load() }, [contactId]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getCrmMembers().then(setMembers).catch(() => {}) }, [])
  const loadCustomDefs = () => getLeadCustomFields().then(setCustomDefs).catch(() => {})
  useEffect(() => { loadCustomDefs() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const reloadTabs = async () => {
    const [h, a, d] = await Promise.allSettled([
      getCrmContactHistory(contactId),
      getCrmContactActivities(contactId),
      getCrmContactDeals(contactId),
    ])
    setHistory(h.status === 'fulfilled' ? h.value : [])
    setActivities(a.status === 'fulfilled' ? a.value : [])
    setDeals(d.status === 'fulfilled' ? d.value : [])
  }

  const ownerName = useMemo(() => {
    if (!contact?.owner_id) return '—'
    return members.find((m) => m.id === contact.owner_id)?.nome || '—'
  }, [contact, members])

  const metrics = useMemo(() => {
    const won = deals
      .filter((d) => d.status === 'ganho')
      .map((d) => ({ valor: Number(d.valor || 0), when: d.data_fechamento || d.updated_at }))
      .filter((d) => d.when)
      .sort((a, b) => new Date(a.when) - new Date(b.when))
    const qtd = won.length
    const total = won.reduce((s, d) => s + d.valor, 0)
    const ticket = qtd > 0 ? total / qtd : 0
    const ultima = qtd > 0 ? won[qtd - 1].when : null
    return { qtd, total, ticket, ultima, ciclo: cicloDias(deals) }
  }, [deals])

  // casa evento do histórico (sem id) com a atividade real (com id), por
  // título+data — só usado pra habilitar "editar" nas notas da timeline.
  const activityIdByKey = useMemo(() => {
    const map = new Map()
    activities.forEach((a) => map.set(`${a.titulo}__${new Date(a.created_at).getTime()}`, a.id))
    return map
  }, [activities])

  const [dealToRemove, setDealToRemove] = useState(null)
  const [removing, setRemoving] = useState(false)
  const confirmRemoveDeal = async () => {
    if (!dealToRemove) return
    setRemoving(true); setErr(null)
    try { await deleteDeal(dealToRemove.id); await reloadTabs(); setDealToRemove(null) }
    catch (e) { setErr(e.message) } finally { setRemoving(false) }
  }

  const handleNewDeal = async () => {
    if (!contact) return
    let pipelineId = null, stageId = null
    try {
      const pls = await getPipelines()
      const def = pls.find((p) => p.is_default) || pls[0]
      if (def) {
        pipelineId = def.id
        const stages = await getStages(def.id)
        stageId = stages[0]?.id || null
      }
    } catch { /* segue sem funil */ }
    setDealModal({ pipelineId, stageId, initialContact: { id: contact.id, nome: contact.nome } })
  }

  const setOrigem = async (v) => {
    setContact((c) => ({ ...c, origem: v }))
    try { await updateCrmContact(contactId, { origem: v }) } catch (e) { setErr(e.message) }
  }

  const handleDelete = async () => {
    setDeleting(true); setErr(null)
    try { await deleteCrmContact(contactId); navigate('/comercial/leads') }
    catch (e) { setErr(e.message); setDeleting(false) }
  }

  if (!selectedClient) {
    return (
      <Layout title="Leads" breadcrumb={[{ label: 'Comercial' }, { label: 'Leads', to: '/comercial/leads' }]}>
        <div className="cv-muted">Selecione um cliente para ver o perfil.</div>
      </Layout>
    )
  }

  const nome = contact?.nome || 'Lead'
  const empresa = contact?.company_nome || contact?.empresa
  const temWhats = !!(contact?.telefone || contact?.whatsapp_id)
  const statusLabel = STATUS_LABELS[contact?.status] || 'Lead'
  const statusColor = STATUS_COLORS[contact?.status] || 'var(--sec)'

  const topbarRight = (
    <>
      {/* Agendar grava uma activity com inicio_at (agenda, migration 034) já
          vinculada a este lead — por isso aparece também na aba Atividades. */}
      <button type="button" className="cv-topbar-btn" onClick={() => setAgendaOpen(true)} disabled={!contact}>
        <Icon n="calendar" s={15} sw={2.2} /> Agendar
      </button>
      <button type="button" className="cv-topbar-btn" onClick={handleNewDeal} disabled={!contact}>
        <Icon n="plus" s={15} sw={2.2} /> Novo negócio
      </button>
      <div className="cv-topbar-more-wrap">
        <button type="button" className="cv-topbar-more" onClick={() => setMoreOpen((v) => !v)} title="Mais ações">
          <Icon n="more" s={17} />
        </button>
        {moreOpen && (
          <div className="cv-topbar-menu" onMouseLeave={() => setMoreOpen(false)}>
            <button type="button" onClick={() => { setMoreOpen(false); setEditOpen(true) }}>
              <Icon n="edit" s={14} /> Editar lead
            </button>
            <button type="button" className="danger" onClick={() => { setMoreOpen(false); setConfirmDelete(true) }}>
              <Icon n="trash" s={14} /> Excluir lead
            </button>
          </div>
        )}
      </div>
    </>
  )

  return (
    <Layout
      title="Leads"
      breadcrumb={[{ label: 'Leads', to: '/comercial/leads' }, { label: nome }]}
      topbarRight={topbarRight}
    >
      {err && <div className="cv-error">{err}</div>}

      {loading ? (
        <div className="cv-muted">Carregando…</div>
      ) : !contact ? (
        <div className="cv-muted">Lead não encontrado.</div>
      ) : (
        <div className="cv-wrap">
          {/* ── Coluna esquerda ─────────────────────────────────────────── */}
          <div className="cv-card cv-left">
            <div className="cv-identity">
              <div className="cv-avatar-wrap">
                <div className="cv-avatar" style={{ background: corAvatar(contact.id) }}>{iniciais(nome)}</div>
                {temWhats && <span className="cv-avatar-dot"><Icon n="whats" s={11} /></span>}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="cv-name">{nome}</div>
                <span className="cv-pill" style={{ color: statusColor, borderColor: statusColor }}>{statusLabel}</span>
              </div>
            </div>

            <div className="cv-rows">
              <div className="cv-row">
                <span className="cv-row-icon"><Icon n="user" s={14} /></span>
                <div><div className="cv-row-label">Proprietário</div><div className="cv-row-value">{ownerName}</div></div>
              </div>
              {contact.telefone && (
                <div className="cv-row">
                  <span className="cv-row-icon"><Icon n="phone" s={14} /></span>
                  <div><div className="cv-row-label">Telefone</div><div className="cv-row-value">{contact.telefone}</div></div>
                </div>
              )}
              {contact.telefone && (
                <div className="cv-row">
                  <span className="cv-row-icon whats"><Icon n="whats" s={14} /></span>
                  <div><div className="cv-row-label">WhatsApp</div><div className="cv-row-value whats">{contact.telefone}</div></div>
                </div>
              )}
            </div>

            <div className="cv-divider" />

            <div className="cv-label">Origem</div>
            <div className="cv-origem-wrap">
              <span className="cv-origem-icon"><Icon n={origemIcon(contact.origem)} s={15} /></span>
              <select className="cv-origem-select" value={contact.origem || ''} onChange={(e) => setOrigem(e.target.value)}>
                <option value="">— sem origem —</option>
                {[...new Set(['Indicação', 'Site', 'WhatsApp', 'Redes sociais', 'Evento', 'Cold outreach', 'Anúncio', 'Parceria', 'Outro', contact.origem].filter(Boolean))].map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <AdOriginCard origin={contact.ad_origin} />

            <LeadCustomFields
              contactId={contact.id}
              definitions={customDefs}
              values={contact.custom_fields}
              onSaved={(custom_fields) => setContact((prev) => ({ ...prev, custom_fields }))}
              onManage={() => setCamposOpen(true)}
            />
            <LeadCustomFieldsManager open={camposOpen} onClose={() => setCamposOpen(false)} onChanged={loadCustomDefs} />

            <button type="button" className="cv-editfields" onClick={() => setEditOpen(true)}>
              <Icon n="edit" s={13} /> Editar campos
            </button>

            <div className="cv-divider" />

            <div className="cv-label">Métricas</div>
            <div className="cv-metrics">
              <div className="cv-metric">
                <div className="cv-metric-head"><span className="cv-metric-icon"><Icon n="currency" s={13} /></span><span className="cv-metric-label">Ticket médio</span></div>
                <div className="cv-metric-value">{fmtBRL(metrics.ticket)}</div>
              </div>
              <div className="cv-metric">
                <div className="cv-metric-head"><span className="cv-metric-icon"><Icon n="currency" s={13} /></span><span className="cv-metric-label">Total</span></div>
                <div className="cv-metric-value">{fmtBRL(metrics.total)}</div>
              </div>
              <div className="cv-metric">
                <div className="cv-metric-head"><span className="cv-metric-icon"><Icon n="refresh" s={13} /></span><span className="cv-metric-label">Ciclo de compra</span></div>
                <div className="cv-metric-value">{metrics.ciclo != null ? `${metrics.ciclo} dias` : '—'}</div>
              </div>
              <div className="cv-metric">
                <div className="cv-metric-head"><span className="cv-metric-icon"><Icon n="calendar" s={13} /></span><span className="cv-metric-label">Última compra</span></div>
                <div className="cv-metric-value">{metrics.ultima ? fmtDate(metrics.ultima) : '—'}</div>
              </div>
            </div>

            <div className="cv-divider" />

            <div className="cv-label">Dados do lead</div>
            <div className="cv-data-row">
              <span className="cv-data-label">Empresa</span>
              <span className="cv-data-value">{empresa || 'Não informada'}</span>
            </div>
            <div className="cv-data-row">
              <span className="cv-data-label">Tags</span>
              <span className="cv-data-value">
                {Array.isArray(contact.tags) && contact.tags.length ? (
                  <span className="cv-tags">{contact.tags.map((t, i) => <span key={i} className="cv-tag">{t}</span>)}</span>
                ) : 'Sem tags'}
              </span>
            </div>
          </div>

          {/* ── Coluna direita ──────────────────────────────────────────── */}
          <div className="cv-card cv-right">
            <div className="cv-tabs">
              <div className="cv-tabs-list">
                {TABS.map((t) => (
                  <button key={t.id} type="button" className={`cv-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                    {t.label}
                    {t.id === 'negocios' && deals.length > 0 && <span className="cv-tab-count">{deals.length}</span>}
                    {t.id === 'atividades' && activities.length > 0 && <span className="cv-tab-count">{activities.length}</span>}
                  </button>
                ))}
              </div>
              <button type="button" className="cv-tabs-close" onClick={() => navigate('/comercial/leads')} title="Voltar para Leads">
                <Icon n="x" s={16} sw={2} />
              </button>
            </div>

            <div className="cv-tabbody">
              {tab === 'historico' && (
                <HistoricoTab
                  contactId={contactId} user={user} history={history} tabsLoading={tabsLoading}
                  activityIdByKey={activityIdByKey} onReload={reloadTabs} onError={setErr}
                />
              )}
              {tab === 'atividades' && (
                <ActivitiesTab
                  items={activities} loading={tabsLoading} members={members}
                  createActivity={(payload) => createCrmContactActivity(contactId, payload)}
                  onCreated={reloadTabs}
                />
              )}
              {tab === 'negocios' && (
                <DealsTab
                  items={deals} loading={tabsLoading}
                  onCreate={handleNewDeal}
                  onOpen={(id) => setInfoDealId(id)}
                  onRemove={setDealToRemove}
                  createLabel="Novo negócio"
                  emptyMsg="Este lead ainda não possui negócios."
                />
              )}
              {tab === 'arquivos' && <FilesTab />}
              {tab === 'atendimentos' && <ServiceTab contactId={contactId} />}
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <ClienteFormPanel
          contactId={contactId}
          entityLabel="lead"
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load() }}
        />
      )}

      {infoDealId && (
        <DealPanel
          dealId={infoDealId}
          onClose={() => setInfoDealId(null)}
          onEdit={() => {}}
          onDeleted={async () => { setInfoDealId(null); await reloadTabs() }}
        />
      )}

      {dealModal && (
        <DealModal
          dealId={null}
          pipelineId={dealModal.pipelineId}
          stageId={dealModal.stageId}
          initialContact={dealModal.initialContact}
          onClose={() => setDealModal(null)}
          onSaved={() => { setDealModal(null); reloadTabs() }}
        />
      )}

      {agendaOpen && contact && (
        <AgendaEventModal
          preset={{ contact_id: contact.id, contact_nome: contact.nome }}
          onClose={() => setAgendaOpen(false)}
          onSaved={() => { setAgendaOpen(false); reloadTabs() }}
        />
      )}

      <ConfirmDialog
        open={!!dealToRemove}
        title="Remover negócio"
        message={dealToRemove ? `Remover o negócio "${dealToRemove.titulo || 'Negócio'}"? Esta ação não pode ser desfeita.` : ''}
        confirmLabel="Remover"
        danger
        busy={removing}
        onConfirm={confirmRemoveDeal}
        onCancel={() => { if (!removing) setDealToRemove(null) }}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Excluir lead"
        message="Excluir este lead? Os vínculos com negócios serão removidos. Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        danger
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => { if (!deleting) setConfirmDelete(false) }}
      />
    </Layout>
  )
}
